import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Empty, NavBar, Popup, SearchBar, Toast } from 'antd-mobile'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../app/providers/AuthProvider'
import { authApi } from '../../features/auth/api/authApi'
import Icon from '../../icon'
import './index.css'

const positions = [
    { value: 1, label: '门将' }, { value: 2, label: '边后卫' },
    { value: 3, label: '中后卫' }, { value: 4, label: '后腰' },
    { value: 5, label: '边前卫' }, { value: 6, label: '前腰' },
    { value: 7, label: '前锋' }, { value: 8, label: '裁判' },
]
const positionName = value => positions.find(item => item.value === Number(value))?.label || '未设置'
const memberPosition = (member, type) => member?.scMemberTypeList?.find(item => Number(item.type) === type)?.memberType ?? null

export default function PositionAudit() {
    const navigate = useNavigate()
    const location = useLocation()
    const { memberInfo } = useAuth()
    const isAdmin = Number(memberInfo?.admin) === 1
    const targetMemberId = location.state?.memberId
    const [members, setMembers] = useState([])
    const [loading, setLoading] = useState(true)
    const [keyword, setKeyword] = useState('')
    const [sortMode, setSortMode] = useState('name')
    const [editingMember, setEditingMember] = useState(null)
    const [mainPosition, setMainPosition] = useState(null)
    const [subPosition, setSubPosition] = useState(null)
    const [saving, setSaving] = useState(false)

    const openEditor = useCallback(member => {
        setEditingMember(member)
        setMainPosition(memberPosition(member, 1))
        setSubPosition(memberPosition(member, 2))
    }, [])

    const loadMembers = useCallback(async () => {
        if (!isAdmin) return
        setLoading(true)
        try {
            const response = await authApi.getAuditMembers()
            if (response.data?.code !== 200) throw new Error(response.data?.msg || '读取球员失败')
            const list = (response.data?.data || []).filter(item => !item.tempMember)
            setMembers(list)
            // 从个人页进入时直接打开该球员，管理员仍可返回列表切换球员。
            if (targetMemberId) {
                const target = list.find(item => String(item.id) === String(targetMemberId))
                if (target) openEditor(target)
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: error?.message || '读取球员失败' })
        } finally {
            setLoading(false)
        }
    }, [isAdmin, openEditor, targetMemberId])

    useEffect(() => { loadMembers() }, [loadMembers])

    const visibleMembers = useMemo(() => {
        const query = keyword.trim().toLowerCase()
        const filtered = query
            ? members.filter(item => `${item.name || ''} ${item.firstNameEn || ''} ${item.memberNum ?? ''}`.toLowerCase().includes(query))
            : members
        // 姓名模式按后端保存的首字母排序；编号模式按数字升序，空编号放末尾。
        return [...filtered].sort((left, right) => {
            if (sortMode === 'number') {
                const leftNum = left.memberNum == null || left.memberNum === '' ? Number.POSITIVE_INFINITY : Number(left.memberNum)
                const rightNum = right.memberNum == null || right.memberNum === '' ? Number.POSITIVE_INFINITY : Number(right.memberNum)
                if (leftNum !== rightNum) return leftNum - rightNum
            } else {
                const leftInitial = String(left.firstNameEn || '').trim()
                const rightInitial = String(right.firstNameEn || '').trim()
                if (!leftInitial && rightInitial) return 1
                if (leftInitial && !rightInitial) return -1
                const initialOrder = leftInitial.localeCompare(rightInitial, 'en', { sensitivity: 'base' })
                if (initialOrder !== 0) return initialOrder
            }
            return String(left.name || '').localeCompare(String(right.name || ''), 'zh-CN')
        })
    }, [keyword, members, sortMode])

    const savePosition = async () => {
        if (!editingMember || saving) return
        if (!mainPosition) return Toast.show('请选择主位置')
        setSaving(true)
        try {
            // 副位置传 0 时后端会清除旧值，页面的“未设置”才能真正保存。
            const updates = [{ type: 1, value: mainPosition }, { type: 2, value: subPosition || 0 }]
            for (const update of updates) {
                const response = await authApi.updateDefaultPosition(editingMember.id, update.type, update.value)
                if (response.data?.code !== 200) throw new Error(response.data?.msg || '保存失败')
            }
            setMembers(previous => previous.map(item => {
                if (String(item.id) !== String(editingMember.id)) return item
                const types = (item.scMemberTypeList || []).filter(type => Number(type.type) !== 1 && Number(type.type) !== 2)
                types.push({ type: 1, memberType: mainPosition })
                if (subPosition) types.push({ type: 2, memberType: subPosition })
                return { ...item, scMemberTypeList: types }
            }))
            setEditingMember(null)
            Toast.show({ icon: 'success', content: '默认位置已保存' })
        } catch (error) {
            Toast.show({ icon: 'fail', content: error?.message || '保存失败，请重试' })
        } finally {
            setSaving(false)
        }
    }

    return <div className="position-audit-page ui-page ui-page--with-nav">
        <NavBar className="unified-navbar fixed" onBack={() => navigate(-1)}>默认位置管理</NavBar>
        <div className="position-audit-content ui-page__content">
            {!isAdmin ? <Empty description="仅管理员可以修改默认位置" /> : <>
                <div className="position-audit-heading"><strong>球员默认位置</strong><span>选择球员，设置主位置和副位置</span></div>
                <div className="position-audit-sticky-tools">
                    <SearchBar placeholder="搜索姓名、首字母或编号" value={keyword} onChange={setKeyword} />
                    <div className="position-audit-toolbar">
                        <span className="position-audit-count">共 {visibleMembers.length} 人</span>
                        <div className="position-audit-sort" role="group" aria-label="球员排序方式">
                            <button type="button" className={sortMode === 'name' ? 'active' : ''} aria-pressed={sortMode === 'name'} onClick={() => setSortMode('name')}>姓名首字母</button>
                            <button type="button" className={sortMode === 'number' ? 'active' : ''} aria-pressed={sortMode === 'number'} onClick={() => setSortMode('number')}>编号</button>
                        </div>
                    </div>
                </div>
                {loading ? <div className="position-audit-empty">正在加载球员…</div> : visibleMembers.length === 0 ? <Empty description="没有找到球员" /> :
                    <div className="position-audit-list">{visibleMembers.map(member => {
                        const avatar = member.scMemberImageList?.[0]?.image
                        const main = memberPosition(member, 1)
                        const sub = memberPosition(member, 2)
                        return <button className="position-audit-card" key={member.id} type="button" onClick={() => openEditor(member)}>
                            <span className="position-audit-card-header">
                                <img className="position-audit-avatar" src={avatar ? avatar + Icon.ImageFooter : Icon.KEPENG_LOGO} alt="" />
                                <span className="position-audit-identity"><strong className="position-audit-name">{member.name || '未命名球员'}</strong><small className="position-audit-number">{member.memberNum == null || member.memberNum === '' ? '未设置编号' : `编号 #${member.memberNum}`}</small></span>
                                <span className="position-audit-edit">编辑 ›</span>
                            </span>
                            {/* 主副位置分开展示，避免在手机窄屏上被省略。 */}
                            <span className="position-audit-position-row">
                                <span className={`position-audit-position position-audit-position--main ${!main ? 'position-audit-position--empty' : ''}`}><small>主位置</small><strong>{positionName(main)}</strong></span>
                                <span className={`position-audit-position position-audit-position--sub ${!sub ? 'position-audit-position--empty' : ''}`}><small>副位置</small><strong>{positionName(sub)}</strong></span>
                            </span>
                        </button>
                    })}</div>}
            </>}
        </div>
        <Popup visible={Boolean(editingMember)} onMaskClick={() => !saving && setEditingMember(null)} bodyStyle={{ borderRadius: '22px 22px 0 0' }}>
            <div className="position-audit-popup">
                <div className="position-audit-popup-title">设置 {editingMember?.name} 的默认位置</div>
                <div className="position-audit-popup-help">主位置为必选；副位置可保持未设置。</div>
                <div className="position-audit-field-title">主位置</div>
                <div className="position-audit-options">{positions.map(option => <button key={option.value} type="button" className={`position-audit-option ${Number(mainPosition) === option.value ? 'selected' : ''}`} onClick={() => setMainPosition(option.value)}>{option.label}</button>)}</div>
                <div className="position-audit-field-title">副位置</div>
                <div className="position-audit-options">
                    <button type="button" className={`position-audit-option ${!subPosition ? 'selected' : ''}`} onClick={() => setSubPosition(null)}>未设置</button>
                    {positions.map(option => <button key={option.value} type="button" className={`position-audit-option ${Number(subPosition) === option.value ? 'selected' : ''}`} onClick={() => setSubPosition(option.value)}>{option.label}</button>)}
                </div>
                <div className="position-audit-actions"><Button onClick={() => setEditingMember(null)} disabled={saving}>取消</Button><Button color="primary" loading={saving} onClick={savePosition}>保存位置</Button></div>
            </div>
        </Popup>
    </div>
}
